import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Database } from "@/integrations/supabase/types";

type AdminModule = Database["public"]["Enums"]["admin_module"];

interface UserPermission {
  module: AdminModule;
  can_view: boolean;
  can_edit: boolean;
}

interface PermissionsContextType {
  permissions: UserPermission[];
  loading: boolean;
  hasPermission: (module: AdminModule, type?: 'view' | 'edit') => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Define module groups for the permissions matrix UI
export const MODULE_GROUPS = {
  "Order Management": [
    { key: "orders" as AdminModule, label: "Orders" },
    { key: "returns" as AdminModule, label: "Returns" },
  ],
  "Customer & Users": [
    { key: "customers" as AdminModule, label: "Customers" },
    { key: "users" as AdminModule, label: "Users" },
    { key: "access_rights" as AdminModule, label: "Access Rights" },
  ],
  "Products & Inventory": [
    { key: "perfumes" as AdminModule, label: "Perfumes" },
    { key: "packaging" as AdminModule, label: "Packaging" },
    { key: "stores" as AdminModule, label: "Stores" },
  ],
  "Marketing & Content": [
    { key: "banners" as AdminModule, label: "Banners" },
    { key: "videos" as AdminModule, label: "Videos" },
    { key: "loading_screens" as AdminModule, label: "Loading Screens" },
    { key: "promos" as AdminModule, label: "Promo Codes" },
    { key: "social_media" as AdminModule, label: "Social Media" },
    { key: "blog" as AdminModule, label: "Blog" },
  ],
  "Site Configuration": [
    { key: "menu" as AdminModule, label: "Menu" },
    { key: "sections" as AdminModule, label: "Homepage Sections" },
    { key: "page_content" as AdminModule, label: "Page Content" },
    { key: "legal" as AdminModule, label: "Legal Pages" },
    { key: "contact" as AdminModule, label: "Contact" },
    { key: "company" as AdminModule, label: "Company Info" },
    { key: "anaya_story" as AdminModule, label: "Anaya Story" },
  ],
  "Quiz & AI": [
    { key: "quiz" as AdminModule, label: "Quiz" },
    { key: "ai_prompts" as AdminModule, label: "AI Prompts" },
    { key: "occasions" as AdminModule, label: "Occasions" },
  ],
  "Settings": [
    { key: "themes" as AdminModule, label: "Themes" },
    { key: "currencies" as AdminModule, label: "Currencies" },
    { key: "countries" as AdminModule, label: "Countries" },
    { key: "address" as AdminModule, label: "Address Fields" },
  ],
  "Reports & Analytics": [
    { key: "reports" as AdminModule, label: "Reports" },
    { key: "usage_analytics" as AdminModule, label: "Usage Analytics" },
  ],
  "E-Commerce": [
    { key: "anaya_collection" as AdminModule, label: "Anaya Collection" },
  ],
  "Payments": [
    { key: "stripe_payments" as AdminModule, label: "Stripe Payments" },
  ],
};

// Get all modules as flat array
export const ALL_MODULES: AdminModule[] = Object.values(MODULE_GROUPS).flat().map(m => m.key);

// Map tab value to module key
export const TAB_TO_MODULE: Record<string, AdminModule> = {
  "orders": "orders",
  "returns": "returns",
  "users": "users",
  "access-rights": "access_rights",
  "promos": "promos",
  "stores": "stores",
  "banners": "banners",
  "videos": "videos",
  "loading-screens": "loading_screens",
  "menu": "menu",
  "sections": "sections",
  "legal": "legal",
  "quiz": "quiz",
  "packaging": "packaging",
  "address": "address",
  "contact": "contact",
  "themes": "themes",
  "currencies": "currencies",
  "countries": "countries",
  "perfumes": "perfumes",
  "ai-prompts": "ai_prompts",
  "occasions": "occasions",
  "company": "company",
  "anaya-story": "anaya_story",
  "reports": "reports",
  "customers": "customers",
  "usage-analytics": "usage_analytics",
  "page-content": "page_content",
  "social-media": "social_media",
  "email-templates": "email_templates",
  "stripe-payments": "stripe_payments",
  "collection": "anaya_collection",
  "blog": "blog",
  "tracking-pixels": "social_media",
};

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAdmin } = useAuth();
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    if (!user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    // Admins have all permissions
    if (isAdmin) {
      const allPerms = ALL_MODULES.map(module => ({
        module,
        can_view: true,
        can_edit: true,
      }));
      setPermissions(allPerms);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_permissions")
        .select("module, can_view, can_edit")
        .eq("user_id", user.id);

      if (error) throw error;

      setPermissions(data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch permissions once we know the admin status
    if (user !== undefined) {
      fetchPermissions();
    }
  }, [user, isAdmin]);

  const hasPermission = (module: AdminModule, type: 'view' | 'edit' = 'view'): boolean => {
    if (isAdmin) return true;
    
    const perm = permissions.find(p => p.module === module);
    if (!perm) return false;
    
    return type === 'edit' ? perm.can_edit : perm.can_view;
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loading,
      hasPermission,
      refreshPermissions: fetchPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const useUserPermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Hook to get a user's permissions for admin management
export const useUserPermissionsAdmin = (userId: string | null) => {
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUserPermissions = async () => {
    if (!userId) {
      setPermissions([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_permissions")
        .select("module, can_view, can_edit")
        .eq("user_id", userId);

      if (error) throw error;
      setPermissions(data || []);
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
  }, [userId]);

  const savePermissions = async (newPermissions: UserPermission[], changedBy: string) => {
    if (!userId) return { error: "No user selected" };

    try {
      // Get current permissions for audit log
      const { data: currentPerms } = await supabase
        .from("user_permissions")
        .select("*")
        .eq("user_id", userId);

      // Delete existing permissions
      await supabase
        .from("user_permissions")
        .delete()
        .eq("user_id", userId);

      // Insert new permissions (only those with at least one permission enabled)
      const permsToInsert = newPermissions
        .filter(p => p.can_view || p.can_edit)
        .map(p => ({
          user_id: userId,
          module: p.module,
          can_view: p.can_view,
          can_edit: p.can_edit,
        }));

      if (permsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("user_permissions")
          .insert(permsToInsert);

        if (insertError) throw insertError;
      }

      // Create audit log entry
      await supabase
        .from("permission_audit_log")
        .insert({
          target_user_id: userId,
          changed_by: changedBy,
          action: "permissions_updated",
          old_value: currentPerms || [],
          new_value: permsToInsert,
        });

      setPermissions(newPermissions);
      return { error: null };
    } catch (error: any) {
      console.error("Error saving permissions:", error);
      return { error: error.message };
    }
  };

  return {
    permissions,
    loading,
    fetchUserPermissions,
    savePermissions,
  };
};
